import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { eventService } from '../services/eventService';
import { validarCupon, translateError } from '../services/api';
import { useTranslation } from 'react-i18next';

const Wrapper = styled.div`
  min-height: 100vh;
  background: #0f0f23;
  color: #e2e8f0;
  padding: 2rem;
`;

const Card = styled.div`
  background: #1a1a35;
  border: 1px solid #2d2d5e;
  border-radius: 20px;
  padding: 2.5rem;
  max-width: 600px;
  margin: 2rem auto;
`;

const Title = styled.h1`
  font-size: 1.8rem;
  font-weight: 800;
  margin: 0 0 0.5rem;
`;

const Subtitle = styled.p`
  color: #94a3b8;
  margin: 0 0 2rem;
`;

const Field = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #94a3b8;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  background: #0f0f23;
  border: 1px solid #2d2d5e;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  color: #e2e8f0;
  font-size: 1rem;
  box-sizing: border-box;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: #7c3aed; }
`;

const Textarea = styled.textarea`
  width: 100%;
  background: #0f0f23;
  border: 1px solid #2d2d5e;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  color: #e2e8f0;
  font-size: 1rem;
  box-sizing: border-box;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.2s;
  &:focus { outline: none; border-color: #7c3aed; }
`;

const Toggle = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ToggleInput = styled.input`
  width: 42px;
  height: 22px;
  cursor: pointer;
`;

const ToggleLabel = styled.span`
  font-size: 0.95rem;
  color: #cbd5e1;
`;

const InfoBox = styled.div`
  background: rgba(124,58,237,0.1);
  border: 1px solid rgba(124,58,237,0.3);
  border-radius: 10px;
  padding: 1rem;
  font-size: 0.9rem;
  color: #a78bfa;
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  color: #e2e8f0;
  margin: 0 0 1rem;
`;

const PlansGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 2rem;
`;

const PlanRow = styled.label<{ selected: boolean; featured: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: ${p => p.selected ? 'rgba(124,58,237,0.15)' : '#0f0f23'};
  border: 2px solid ${p => p.selected ? '#7c3aed' : p.featured ? '#4c1d95' : '#2d2d5e'};
  border-radius: 14px;
  padding: 1rem 1.2rem;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  position: relative;
  &:hover { border-color: #7c3aed; }
`;

const PlanRadio = styled.input`
  width: 18px;
  height: 18px;
  accent-color: #7c3aed;
  cursor: pointer;
  flex-shrink: 0;
`;

const PlanInfo = styled.div`
  flex: 1;
`;

const PlanName = styled.div`
  font-weight: 700;
  font-size: 1rem;
  color: #e2e8f0;
`;

const PlanFeatures = styled.div`
  font-size: 0.82rem;
  color: #94a3b8;
  margin-top: 0.2rem;
`;

const PlanPrice = styled.div`
  font-size: 1.3rem;
  font-weight: 900;
  color: #7c3aed;
  white-space: nowrap;
`;

const PlanBadge = styled.span`
  position: absolute;
  top: -10px;
  right: 12px;
  background: linear-gradient(135deg, #7c3aed, #ec4899);
  color: white;
  padding: 0.15rem 0.7rem;
  border-radius: 20px;
  font-size: 0.72rem;
  font-weight: 700;
`;

const CouponRow = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
`;

const CouponInput = styled(Input)`
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const CouponBtn = styled.button<{ loading?: boolean }>`
  background: #2d2d5e;
  color: #e2e8f0;
  border: 1px solid #4c4c8a;
  border-radius: 10px;
  padding: 0.75rem 1.2rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.2s;
  &:hover { background: #3d3d7a; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CouponMsg = styled.div<{ ok: boolean }>`
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: ${p => p.ok ? '#10b981' : '#ef4444'};
`;

const PriceSummary = styled.div`
  background: #0f0f23;
  border: 1px solid #2d2d5e;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.25rem 0;
  color: #94a3b8;
`;

const PriceTotal = styled(PriceRow)`
  font-size: 1.1rem;
  font-weight: 700;
  color: #e2e8f0;
  border-top: 1px solid #2d2d5e;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
`;

const SubmitBtn = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #7c3aed, #ec4899);
  color: white;
  border: none;
  border-radius: 12px;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.2s;
  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 1.5rem;
  color: #94a3b8;
  text-decoration: none;
  font-size: 0.9rem;
  &:hover { color: #e2e8f0; }
`;

const Error = styled.p`
  color: #ef4444;
  font-size: 0.9rem;
  margin-top: 0.75rem;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #2d2d5e;
  margin: 2rem 0;
`;

const DESC_MAX = 500;

const d = new Date();
const today = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;

const CreateEventPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const PLANS = [
    { key: 'Básico',    price: 499,  featured: false, featKey: 'basico' },
    { key: 'Advanced',  price: 899,  featured: true,  featKey: 'advanced' },
    { key: 'Pro',       price: 1599, featured: false, featKey: 'pro' },
    { key: 'Platinum',  price: 2699, featured: false, featKey: 'platinum' },
  ];

  const [form, setForm] = useState({ name: '', eventDate: '', description: '', moderationEnabled: false });
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [couponCode, setCouponCode] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponResult, setCouponResult] = useState<{ ok: boolean; msg: string; pct?: number; code?: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const dateRef = useRef<HTMLInputElement>(null);

  const openDatePicker = () => { try { dateRef.current?.showPicker(); } catch {} };

  const plan = PLANS.find(p => p.key === selectedPlan);
  const originalAmount = plan?.price ?? 0;
  const discountPct = couponResult?.ok ? (couponResult.pct ?? 0) : 0;
  const discountAmount = Math.round(originalAmount * discountPct / 100);
  const finalAmount = originalAmount - discountAmount;

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponResult(null);
    try {
      const res = await validarCupon(couponCode.trim());
      if (res.valid) {
        setCouponResult({ ok: true, msg: `✓ ${res.porcentajeDescuento}% de descuento aplicado`, pct: res.porcentajeDescuento, code: res.codigo });
      } else {
        const msg = translateError({ response: { data: { error: res.message } } }, t('create_event.coupon_invalid'));
        setCouponResult({ ok: false, msg });
      }
    } catch {
      setCouponResult({ ok: false, msg: t('create_event.err_coupon') });
    } finally {
      setCouponLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError(t('create_event.err_name')); return; }
    if (!form.eventDate) { setError(t('create_event.err_date')); return; }
    if (!form.description.trim()) { setError(t('create_event.err_desc')); return; }
    if (form.description.length > DESC_MAX) { setError(t('create_event.err_desc_max', { max: DESC_MAX })); return; }
    if (!selectedPlan) { setError(t('create_event.err_plan')); return; }

    if (discountPct === 100) {
      setLoading(true);
      try {
        const event = await eventService.createEvent({
          name: form.name, eventDate: form.eventDate || undefined,
          description: form.description || undefined,
          moderationEnabled: form.moderationEnabled, plan: selectedPlan,
        });
        navigate(`/dashboard/evento/${event.id}`, { state: { justCreated: true } });
      } catch {
        setError(t('create_event.err_create'));
      } finally { setLoading(false); }
      return;
    }

    navigate('/dashboard/pagar', {
      state: {
        eventData: { name: form.name, eventDate: form.eventDate, description: form.description, moderationEnabled: form.moderationEnabled },
        plan: selectedPlan, originalAmount,
        discountPct: couponResult?.ok ? discountPct : undefined,
        finalAmount, couponCode: couponResult?.ok ? couponResult.code : undefined,
      }
    });
  };

  return (
    <Wrapper>
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <BackLink to="/dashboard">{t('create_event.back')}</BackLink>
      </div>
      <Card>
        <Title>{t('create_event.title')}</Title>
        <Subtitle>{t('create_event.sub')}</Subtitle>

        <form onSubmit={handleSubmit}>
          <Field>
            <Label>{t('create_event.label_name')}</Label>
            <Input type="text" placeholder={t('create_event.placeholder_name')} value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })} />
          </Field>

          <Field>
            <Label>{t('create_event.label_date')}</Label>
            <Input ref={dateRef} type="date" value={form.eventDate} min={today}
              onClick={openDatePicker} onFocus={openDatePicker} onKeyDown={openDatePicker}
              onChange={e => setForm({ ...form, eventDate: e.target.value })} />
          </Field>

          <Field>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <Label style={{ margin: 0 }}>{t('create_event.label_desc')}</Label>
              <span style={{ fontSize: '0.8rem', color: form.description.length > DESC_MAX ? '#ef4444' : '#64748b' }}>
                {form.description.length}/{DESC_MAX}
              </span>
            </div>
            <Textarea placeholder={t('create_event.placeholder_desc')} value={form.description}
              maxLength={DESC_MAX} onChange={e => setForm({ ...form, description: e.target.value })} />
          </Field>

          <Field>
            <Label>{t('create_event.label_moderation')}</Label>
            <Toggle>
              <ToggleInput type="checkbox" checked={form.moderationEnabled}
                onChange={e => setForm({ ...form, moderationEnabled: e.target.checked })} />
              <ToggleLabel>
                {form.moderationEnabled ? t('create_event.mod_on') : t('create_event.mod_off')}
              </ToggleLabel>
            </Toggle>
          </Field>

          <InfoBox>{t('create_event.info_box')}</InfoBox>
          <Divider />

          <SectionTitle>{t('create_event.plan_title')}</SectionTitle>
          <PlansGrid>
            {PLANS.map(p => (
              <PlanRow key={p.key} selected={selectedPlan === p.key} featured={p.featured}>
                {p.featured && <PlanBadge>{t('create_event.plan_popular')}</PlanBadge>}
                <PlanRadio type="radio" name="plan" value={p.key} checked={selectedPlan === p.key}
                  onChange={() => setSelectedPlan(p.key)} />
                <PlanInfo>
                  <PlanName>{p.key}</PlanName>
                  <PlanFeatures>{t(`create_event.plan_features.${p.featKey}`)}</PlanFeatures>
                </PlanInfo>
                <PlanPrice>${p.price} <span style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 400 }}>MXN</span></PlanPrice>
              </PlanRow>
            ))}
          </PlansGrid>
          <Divider />

          <Field>
            <Label>{t('create_event.label_coupon')}</Label>
            <CouponRow>
              <CouponInput type="text" placeholder="" value={couponCode}
                onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponResult(null); }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleValidateCoupon(); } }} />
              <CouponBtn type="button" onClick={handleValidateCoupon} disabled={couponLoading || !couponCode.trim()}>
                {couponLoading ? '...' : t('create_event.btn_apply')}
              </CouponBtn>
            </CouponRow>
            {couponResult && <CouponMsg ok={couponResult.ok}>{couponResult.msg}</CouponMsg>}
          </Field>

          {plan && (
            <PriceSummary>
              <PriceRow>
                <span>{t('create_event.price_plan', { name: plan.key })}</span>
                <span>${originalAmount.toFixed(2)} MXN</span>
              </PriceRow>
              {couponResult?.ok && discountPct > 0 && (
                <PriceRow style={{ color: '#10b981' }}>
                  <span>{t('create_event.price_discount', { code: couponResult.code, pct: discountPct })}</span>
                  <span>-${discountAmount.toFixed(2)} MXN</span>
                </PriceRow>
              )}
              <PriceTotal>
                <span>{t('create_event.price_total')}</span>
                <span style={{ color: finalAmount === 0 ? '#10b981' : '#7c3aed' }}>
                  {finalAmount === 0 ? t('create_event.price_free') : `$${finalAmount.toFixed(2)} MXN`}
                </span>
              </PriceTotal>
            </PriceSummary>
          )}

          {error && <Error>{error}</Error>}

          <SubmitBtn type="submit" disabled={loading}>
            {loading ? t('create_event.btn_creating') : discountPct === 100 ? t('create_event.btn_free') : t('create_event.btn_pay')}
          </SubmitBtn>
        </form>
      </Card>
    </Wrapper>
  );
};

export default CreateEventPage;
